import { Button, Collapse, ConfirmModal, Tag } from '@vrplatform/components';
import {
  captureSentryError,
  fetchApiAllItems,
  showApiErrorNotification,
  useApiMutation,
  useApiQuery,
  useInvalidateApiQueries,
  useTeam,
} from '@vrplatform/data';
import { Edit3Icon, Icon } from '@vrplatform/icons';
import { LazyTable, type MRT_ColumnDef } from '@vrplatform/table';
import {
  Drawer,
  EllipsisMenuCopyItem,
  EllipsisMenuDangerItem,
  EllipsisMenuDivider,
  EllipsisMenuItem,
  LoadingIndicator,
} from '@vrplatform/components-legacy';
import { showWarnNotification, type ThemeColor } from '@vrplatform/theme';
import { day, formatCurrency, sortBy } from '@vrplatform/utils';
import { Box, Center, Group, Text, rem } from '@mantine/core';
import {
  formatOwnerName,
  formatOwnershipBusinessModel,
} from '@vrplatform/ui-common';
import { type ComponentProps, useMemo, useState } from 'react';
import { HiddenFeatureIndicator, OwnerBadge } from '../../../../components';
import { DrawerHeader } from '../../../../drawers/_components';
import { useListingDetailDrawer } from '../../drawer';
import { ListingPeriodForm } from '../../drawer/ListingPeriodForm';

type Props = {
  listingId: string;
};

export const ListingPeriodsTable = (props: Props) => {
  const queryData = useListingPeriodsTableQuery(props);
  const [{ currency }] = useTeam();

  const { listingId } = props;

  const [opened, setOpened] = useState<{
    period: PeriodRow;
    modal: 'edit' | 'delete';
  } | null>(null);

  const closeModal = () => setOpened(null);

  const columns = useMemo<MRT_ColumnDef<PeriodRow>[]>(() => {
    return [
      {
        header: 'Period',
        accessorKey: 'startAt',
        size: 250,
        minSize: 250,
        maxSize: 0,
        Cell: ({ row }) => {
          const period = row.original;
          const startAt = day(period.startAt).format('ll');
          const endAt = period.endAt
            ? day(period.endAt).format('ll')
            : 'forever';

          return (
            <Group wrap="nowrap" gap={rem(8)}>
              <Text>{startAt}</Text>

              <Icon
                icon="ArrowRightIcon"
                color={(theme) => theme.colors.gray[5]}
                size={13}
                strokeWidth={2}
              />

              <Text span c="gray">
                {endAt}
              </Text>
            </Group>
          );
        },
      },
      {
        header: 'Status',
        size: 250,
        minSize: 250,
        maxSize: 0,
        Cell: ({ row }) => {
          const start = row.original.startAt;
          const end = row.original.endAt;

          if (!start && !end) return null;
          let config: { color: ThemeColor; label: string } = {
            color: 'gray',
            label: 'Past',
          };

          const isPast = end && day(end).isBefore(day());
          const isFuture = day(start).isAfter(day());

          if (isPast) {
            config = { color: 'gray', label: 'Past' };
          } else if (isFuture) {
            config = { color: 'yellow', label: 'Future' };
          } else {
            config = { color: 'green', label: 'Current' };
          }

          const tag = (
            <Tag
              leftIcon={() => (
                <Center
                  sx={(theme) => ({
                    backgroundColor: theme.colors[config.color][5],
                    width: rem(6),
                    height: rem(6),
                    borderRadius: '100%',
                  })}
                />
              )}
            >
              {config.label}
            </Tag>
          );

          if (row.original.setListingInactive)
            return (
              <Group gap={rem(5)}>
                {tag}
                <Tag>Deactivated</Tag>
              </Group>
            );

          return tag;
        },
      },
      {
        header: 'Business model',
        size: 200,
        minSize: 200,
        maxSize: 0,
        Cell: ({ row }) => {
          if (row.original.setListingInactive) return null;

          const businessModel = row.original.businessModel;

          return (
            <Tag leftIcon={businessModel ? 'WrenchIcon' : 'UsersIcon'}>
              {formatOwnershipBusinessModel(row.original.businessModel)}
            </Tag>
          );
        },
      },
      {
        header: 'Owners',
        Cell: ({ row }) => {
          return (
            <Group gap={rem(5)}>
              {row.original.members.map((member) => {
                return (
                  <OwnerBadge
                    key={member.owner.id}
                    ownerId={member.owner.id}
                    type={member.owner.type}
                    name={member.owner.name}
                    splitSuperBasePoints={member.split}
                  />
                );
              })}
            </Group>
          );
        },
      },
      {
        header: 'Owner reserve',
        size: 170,
        minSize: 170,
        maxSize: 0,
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
        },
        Cell: ({ row }) =>
          formatCurrency((row.original.reserve ?? 0) / 100, currency),
      },
    ];
  }, [currency]);

  const openEditCurrentPeriod = useMemo(() => {
    const currentPeriod = queryData.data?.[0];

    if (!currentPeriod || currentPeriod.setListingInactive) return undefined;

    return () => setOpened({ period: currentPeriod, modal: 'edit' });
  }, [queryData.data?.[0]]);

  return (
    <>
      <Box mb="xl">
        <Collapse
          defaultOpened
          hideToggle
          title={'Ownership'}
          rightSection={
            <CollapseAction
              {...props}
              openEditCurrentPeriod={openEditCurrentPeriod}
            />
          }
        >
          <Box>
            <LazyTable
              data={{
                error: queryData.error?.message,
                loading: queryData.isLoading,
                rowCount: queryData.data?.length ?? 0,
                rows: queryData.data ?? [],
                isFetching: queryData.isFetching,
              }}
              table={{
                columns,
                emptyRowsFallback: 'No ownerships',
                hidePagination: true,
                minHeight: 100,
                hideSettings: true,
              }}
              rowMenu={{
                menuItems: ({ row }) => {
                  const period = row.original;

                  return (
                    <>
                      {!period.setListingInactive && (
                        <>
                          <EllipsisMenuItem
                            customIcon={<Edit3Icon size={16} />}
                            onClick={() => setOpened({ period, modal: 'edit' })}
                          >
                            Edit
                          </EllipsisMenuItem>
                          <EllipsisMenuDivider />
                        </>
                      )}
                      <EllipsisMenuDangerItem
                        onClick={() => setOpened({ period, modal: 'delete' })}
                      >
                        Delete
                      </EllipsisMenuDangerItem>
                      <HiddenFeatureIndicator permission="vrp-admin">
                        <EllipsisMenuCopyItem value={row.original.id} />
                      </HiddenFeatureIndicator>
                    </>
                  );
                },
              }}
            />
          </Box>
        </Collapse>
      </Box>

      <Delete
        opened={opened?.modal === 'delete'}
        period={opened?.period}
        closeModal={closeModal}
      />

      <EditPeriodDrawer
        closeModal={closeModal}
        listingId={listingId}
        period={opened?.period}
        opened={opened?.modal === 'edit'}
      />
    </>
  );
};

type PeriodFormInput = NonNullable<
  ComponentProps<typeof ListingPeriodForm>['initialValues']
>;

const EditPeriodDrawer = ({
  closeModal,
  period,
  listingId,
  opened,
}: {
  period: PeriodRow | undefined;
  closeModal: () => void;
  listingId: string;
  opened: boolean;
}) => {
  const { data, isLoading } = useApiQuery(
    async (api, signal, args) => {
      if (!args.periodId) {
        return {
          currentPeriod: null,
        };
      }

      const response = await api.GET('/listings/ownership-periods/{id}', {
        params: {
          path: {
            id: args.periodId,
          },
        },
        signal,
      });

      if (response.error) {
        throw response.error;
      }

      return {
        currentPeriod: {
          id: response.data.id,
          startDate: response.data.startAt,
          endDate: response.data.endAt,
          businessModel: response.data.businessModel ?? 'managed',
          reserveCentTotal: response.data.reserve ?? 0,
          members: response.data.members.map((member) => ({
            ownerId: member.contactId ?? null,
            splitBasePoints: member.split ?? 0,
          })),
        } satisfies PeriodFormInput,
      };
    },
    {
      skip: !period || !opened,
      queryGroup: 'listings',
      uniqueQueryKey: 'listing-periods-table',
      queryKeys: ['listings'],
      variables: {
        periodId: period?.id,
      },
    }
  );

  return (
    <Drawer opened={opened} onClose={closeModal}>
      <DrawerHeader
        closeDrawer={closeModal}
        title={'Edit Period'}
        type="Listing"
        loading={isLoading}
      />
      {isLoading ? (
        <LoadingIndicator isFullPageLoading />
      ) : data?.currentPeriod ? (
        <ListingPeriodForm
          initialValues={data.currentPeriod}
          onReset={closeModal}
          loadingQuery={false}
          previousValues={undefined}
          listingId={listingId}
        />
      ) : (
        <Text ta="center">Failed to find the listing ownership.</Text>
      )}
    </Drawer>
  );
};

const CollapseAction = ({
  listingId,
  openEditCurrentPeriod,
}: Props & {
  openEditCurrentPeriod: (() => void) | undefined;
}) => {
  const { open } = useListingDetailDrawer();

  return (
    <Group>
      {openEditCurrentPeriod && (
        <Button
          leftIcon={'Edit3Icon'}
          variant="light"
          onClick={openEditCurrentPeriod}
        >
          Edit current
        </Button>
      )}
      <Button
        leftIcon={'PlusIcon'}
        variant="light"
        onClick={() => open(listingId, 'record-ownership')}
      >
        Add upcoming
      </Button>
    </Group>
  );
};

const Delete = ({
  closeModal,
  period,
  opened,
}: {
  period: PeriodRow | undefined;
  closeModal: () => void;
  opened: boolean;
}) => {
  const invalidate = useInvalidateApiQueries(['listings']);

  const { mutateAsync, isPending: loading } = useApiMutation(
    'delete',
    '/listings/ownership-periods/{id}'
  );

  const removePeriod = async () => {
    if (!period) {
      captureSentryError('No period found to delete!');
      showWarnNotification({
        message:
          'We failed to delete the ownership period. Please try again and if the issue persists, contact support.',
      });

      return;
    }

    await mutateAsync({
      params: {
        path: {
          id: period.id,
        },
      },
    })
      .then(() => {
        // TODO: update to ownership_period_deleted
        // track("ownership_period_deleted", {
        //   listingId,
        // })

        closeModal();
        invalidate();
      })
      .catch((error: any) => {
        showApiErrorNotification({
          title: 'Failed to delete ownership',
          error,
        });
      });
  };

  return (
    <ConfirmModal
      type="delete"
      loading={loading}
      opened={opened}
      closeModal={closeModal}
      title={'Are you sure to delete this ownership period?'}
      subtitle="Once deleted all data will be lost."
      onSubmit={removePeriod}
    />
  );
};

type PeriodRow = NonNullable<
  ReturnType<typeof useListingPeriodsTableQuery>['data']
>[number];

type ListingOwnershipContact = {
  id: string;
  name?: string | null;
  firstName?: string | null;
  companyType?: string | null;
};

function useListingPeriodsTableQuery({ listingId }: Props) {
  return useApiQuery(
    async (api, signal, args, hasura) => {
      if (!args.listingId) return [];

      const periods = await fetchApiAllItems(async (page) => {
        const response = await api.GET('/listings/ownership-periods', {
          params: {
            query: {
              listingId: args.listingId,
              page,
            },
          },
          signal,
        });
        if (response.error) {
          throw response.error;
        }

        return {
          items: response.data.data,
          hasNextPage: !!response.data.pagination.nextPage,
        };
      });

      const contactIds = Array.from(
        new Set(
          periods.flatMap((period) =>
            period.members
              .map((member) => member.contactId)
              .filter((contactId): contactId is string => !!contactId)
          )
        )
      );

      let contactsById = new Map<string, ListingOwnershipContact>();

      if (contactIds.length) {
        const { contacts } = await hasura((q) => ({
          contacts: q
            .contacts({
              where: {
                id: {
                  _in: contactIds,
                },
              },
            })
            .map((contact) => ({
              id: contact.id,
              name: contact.name,
              firstName: contact.firstName,
              companyType: contact.companyType,
            })),
        }));

        contactsById = new Map(
          contacts.map((contact) => [contact.id, contact])
        );
      }

      return sortBy(periods, 'startAt', 'desc').map((period) => {
        const members = period.members
          .filter((member) => !!member.contactId)
          .map((member) => {
            const contactId = member.contactId as string;
            const contact = contactsById.get(contactId);
            const name =
              formatOwnerName(contact, {
                showEmpty: true,
                lastNameFirst: false,
              }) || '-';

            return {
              id: contactId,
              split: member.split ?? 0,
              owner: {
                id: contactId,
                type: contact?.companyType
                  ? ('company' as const)
                  : ('individual' as const),
                name,
              },
            };
          });

        return {
          id: period.id,
          setListingInactive: period.setListingInactive,
          startAt: period.startAt,
          endAt: period.endAt,
          reserve: period.reserve ?? 0,
          businessModel: period.businessModel ?? 'managed',
          members: sortBy(members, (x) => x.owner.name, 'asc'),
        };
      });
    },
    {
      queryGroup: 'listings',
      uniqueQueryKey: 'listings',
      queryKeys: ['listings'],
      keepPreviousData: true,
      skip: !listingId,
      variables: {
        listingId,
      },
    }
  );
}
